import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { apiClient } from "../../../../api/httpClient";
import {
  formatAsCurrency,
  parseCurrencyToNumber,
} from "../formHelpers";
import {
  calculateAgeFromDate,
  applyRetrievalStandardPremium,
  applyRetrievalGuardianshipPremium,
} from "../retrievalBasicInfoPremiumHelpers";

function warnMinorAgeIfNeeded(name, value, bondSubCategory, minorAgeValidation) {
  if (!minorAgeValidation || name !== "dateOfDeath" || bondSubCategory !== "Guardian - Minor") {
    return;
  }
  const age = calculateAgeFromDate(value);
  if (age >= 18) {
    toast.error("Ward must be under 18 years old for Guardian - Minor bonds");
  }
}

function getPremiumTriggerFields(premiumStrategy) {
  return premiumStrategy === "guardianship"
    ? ["bondAmount", "dateOfDeath"]
    : ["bondAmount"];
}

function applyParsedBondAmount(updatedData, name, value, parseGuardianshipBondAmount) {
  if (name !== "bondAmount" || !parseGuardianshipBondAmount) return updatedData;
  const numericValue = parseCurrencyToNumber(value);
  if (!Number.isFinite(numericValue)) return updatedData;
  return { ...updatedData, bondAmount: numericValue };
}

export function useBondRetrievalBasicInfoState({ data, updateData, config }) {
  const [state, setState] = useState(null);
  const [counties, setCounties] = useState([]);
  const [loadingCounties, setLoadingCounties] = useState(false);

  const {
    premiumStrategy,
    defaultCountField,
    minorAgeValidation,
    showCounty,
    countiesFetchMode,
    parseGuardianshipBondAmount,
    hydrateStorageOnMount,
  } = config;

  const recalcPremium = (updatedData, withDefaultCount = true) => {
    if (premiumStrategy === "guardianship") {
      const withPremium = applyRetrievalGuardianshipPremium(updatedData);
      if (!withPremium) return null;
      if (!withDefaultCount) return withPremium;
      return { ...withPremium, [defaultCountField]: "1" };
    }
    return applyRetrievalStandardPremium(updatedData, defaultCountField);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    warnMinorAgeIfNeeded(name, value, data.bondSubCategory, minorAgeValidation);

    const premiumTriggerFields = getPremiumTriggerFields(premiumStrategy);

    if (!premiumTriggerFields.includes(name)) {
      updateData({ ...data, [name]: value });
      return;
    }

    let updatedData = { ...data, [name]: value };
    updatedData = applyParsedBondAmount(
      updatedData,
      name,
      value,
      parseGuardianshipBondAmount
    );

    updateData(updatedData);
    const withDefaultCount = premiumStrategy !== "guardianship" && name === "bondAmount";
    const withPremium = recalcPremium(updatedData, withDefaultCount);
    if (withPremium) updateData(withPremium);
  };

  const handleAutocompleteChange = (event, value, fieldName) => {
    const updatedData = { ...data, [fieldName]: value };
    updateData(updatedData);

    if (
      premiumStrategy === "guardianship" &&
      fieldName === "bondSubCategory" &&
      updatedData.bondAmount &&
      updatedData.dateOfDeath
    ) {
      const withPremium = recalcPremium(updatedData, false);
      if (withPremium) updateData(withPremium);
    }
  };

  useEffect(() => {
    if (!hydrateStorageOnMount) return undefined;

    const savedState = localStorage.getItem("selectedState");
    const savedType = localStorage.getItem("selectedBondType");

    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        if (showCounty) setState(parsedState);
        updateData({ ...data, state: parsedState.label });
      } catch {
        // ignore malformed localStorage
      }
    }

    if (savedType) {
      try {
        const parsedType = JSON.parse(savedType);
        updateData({ ...data, bondType: parsedType.label });
      } catch {
        // ignore malformed localStorage
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showCounty) return undefined;

    const fetchCounties = async () => {
      setLoadingCounties(true);
      try {
        if (countiesFetchMode === "all") {
          const countyResponse = await apiClient("/counties/", "GET");
          setCounties(countyResponse.map((county) => county.bond_county));
        } else if (state?.id) {
          const countyResponse = await apiClient(`/counties-by-state/${state.id}/`, "GET");
          setCounties(countyResponse.map((county) => county.bond_county));
        }
      } catch (error) {
        console.error("Error fetching counties:", error);
      } finally {
        setLoadingCounties(false);
      }
    };

    fetchCounties();
  }, [state, showCounty, countiesFetchMode]);

  return {
    counties,
    loadingCounties,
    formatAsCurrency,
    handleChange,
    handleAutocompleteChange,
  };
}
